/**
 * "Can I actually train this yet?" — surfaced where plans get written.
 *
 * A pull up is the one prescription the app can check the arithmetic on before
 * anyone attempts it: the load is your body weight, and the app knows both your
 * body weight and what you have pulled. The verdicts come from the server
 * (`/readiness`, the same call `/ai/plan` makes internally) so the generated
 * plan and the hand-written one give identical advice — two copies of this rule
 * would drift within a month.
 *
 * The server sends numbers and a verdict; the sentence is composed here. Its
 * own `why` string is written in the third person for the model, and a screen
 * that addresses you directly can't reuse a sentence written about you to
 * somebody else.
 *
 * Needs a connection, and says nothing without one. This is advice, not data —
 * an empty space is the correct offline rendering of it, and the plan builder
 * behind it keeps working either way.
 */

import { useEffect, useState } from 'react'

import { api } from '../api/client'
import { formatWeight, type Unit } from '../lib/units'

export type Verdict = 'ready' | 'close' | 'not_ready' | 'unknown'

export type ReadinessMovement = {
  movement: string
  also_covers: string[]
  pattern: string
  verdict: Verdict
  use_instead: string | null
  also_ok: string[]
  ratio: number | null
  why: string | null
  evidence: {
    kind: 'logged' | 'proxy' | 'assist' | null
    bodyweight_kg: number | null
    reps?: number
    proxy?: string
    proxy_1rm_kg?: number
    needs_ratio?: number
    assist_kg?: number
    on?: string
  }
}

/**
 * Working sets per category over the last 28 days — the same counts the plan
 * generator is handed. `null` when too little has been logged to judge, which
 * is also when the model isn't told either; the composer stays quiet rather
 * than promising a weighting that won't happen.
 */
export type TrainingBalance = {
  window_days: number
  total_working_sets: number
  by_category: { category: string; sets: number; share_pct: number }[]
}

export type Readiness = {
  unit: string
  bodyweight: {
    weight_kg: number
    measured_at: string
    source: 'weigh_in' | 'workout'
    is_stale: boolean
  } | null
  movements: ReadinessMovement[]
  balance: TrainingBalance | null
}

/**
 * undefined = still asking, null = couldn't (offline, or the request failed).
 *
 * The two are kept apart for the usual reason: conflated, a screen that renders
 * a spinner while undefined spins forever the moment the answer is legitimately
 * "no".
 */
export function useReadiness(): Readiness | null | undefined {
  const [state, setState] = useState<Readiness | null | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    api<Readiness>('/readiness')
      .then((r) => { if (!cancelled) setState(r) })
      .catch(() => { if (!cancelled) setState(null) })
    return () => { cancelled = true }
  }, [])
  return state
}

/** The verdict covering an exercise name, if there is one. */
export function verdictFor(
  readiness: Readiness | null | undefined,
  exerciseName: string | null | undefined,
): ReadinessMovement | null {
  if (!readiness || !exerciseName) return null
  const name = exerciseName.toLowerCase()
  return readiness.movements.find(
    (m) => m.movement.toLowerCase() === name
      || m.also_covers.some((n) => n.toLowerCase() === name),
  ) ?? null
}

/**
 * Why the app thinks so, addressed to the person reading it.
 *
 * Written to be checkable rather than authoritative — it shows the two numbers
 * it compared, so somebody who disagrees can see exactly what it used and go
 * and prove it wrong, which is the point.
 */
export function explain(m: ReadinessMovement, unit: Unit): string {
  const e = m.evidence
  const bw = e.bodyweight_kg ? formatWeight(e.bodyweight_kg, unit) : null

  switch (e.kind) {
    case 'logged':
      return m.verdict === 'ready'
        ? `You've logged ${e.reps} in a set.`
        : `Your best set is ${e.reps} rep${e.reps === 1 ? '' : 's'}.`
    case 'assist':
      return `You're still taking ${formatWeight(e.assist_kg, unit)} of help on the ${
        (e.on ?? 'assist').toLowerCase()}${bw ? `, against ${bw} of you` : ''}.`
    case 'proxy':
      return `Your ${(e.proxy ?? '').toLowerCase()} works out to ${
        formatWeight(e.proxy_1rm_kg, unit)} for one rep${bw ? `, and you weigh ${bw}` : ''}${
        m.ratio !== null ? ` — ${Math.round(m.ratio * 100)}% of the way there` : ''}.`
    default:
      return m.verdict === 'unknown' ? '' : `${m.movement} may be a stretch right now.`
  }
}

/**
 * The note itself. Renders nothing at all unless there is something to say —
 * `ready` and `unknown` are both silence, because an app that comments on every
 * exercise you pick is an app you stop reading.
 */
export function ReadinessNote({
  movement,
  unit,
  onSwap,
}: {
  movement: ReadinessMovement
  unit: Unit
  /** Offered only when the substitute is in the library and pickable here. */
  onSwap?: (substitute: string) => void
}) {
  if (movement.verdict !== 'not_ready' && movement.verdict !== 'close') return null
  const sub = movement.use_instead

  return (
    <div
      style={{
        marginTop: '.4rem',
        padding: '.5rem .6rem',
        borderRadius: 'var(--radius)',
        background: 'var(--surface-2)',
        fontSize: '.82rem',
      }}
    >
      <strong style={{ fontWeight: 600 }}>{movement.movement}</strong>{' '}
      <span className="muted">
        {movement.verdict === 'not_ready'
          ? `— ${sub ?? 'an assisted version'} is the version you can train today. `
          : `— close. Keep it for low reps and use ${sub ?? 'the assisted version'} for the volume. `}
        {explain(movement, unit)}
      </span>
      {sub && onSwap && (
        <button
          className="secondary"
          style={{
            marginTop: '.4rem', padding: '.2rem .6rem', minHeight: 0,
            fontSize: '.8rem', display: 'block',
          }}
          onClick={() => onSwap(sub)}
        >
          Use {sub} instead
        </button>
      )}
    </div>
  )
}
